/**
 * Portal Permission Utility Functions
 * Centralized role-based access control for the portal system
 *
 * Roles: developer, system_admin, director, manager, admin, teacher, parent
 * Note: developer is a hidden god-mode role (not visible in user management)
 * Note: director is campus-restricted (like manager/admin)
 */

/**
 * Helper: check if a group's campus matches the user's campus(es)
 * Supports both single campus string and campusIds array
 */
const matchesCampus = (groupCampusId, userCampus, userCampusIds) => {
  if (userCampusIds && userCampusIds.length > 0) {
    return userCampusIds.includes(groupCampusId);
  }
  return groupCampusId === userCampus;
};

/**
 * Check if user can view a specific course group
 * @param {string} role - User's role
 * @param {object} group - Course group object
 * @param {number} userId - Current user ID
 * @param {string} userCampus - User's campus (for manager)
 * @param {array} userChildrenCourses - Array of course IDs for parent's children
 * @returns {boolean}
 */
export const canViewGroup = (role, group, userId, userCampus = null, userChildrenCourses = [], userCampusIds = []) => {
  switch (role) {
    case 'developer':
    case 'system_admin':
      // Can view all groups
      return true;

    case 'director':
    case 'manager':
    case 'admin':
      // Can only view groups in their campus(es)
      return matchesCampus(group.campusId, userCampus, userCampusIds);

    case 'teacher':
      // Can only view groups they teach
      return group.teacherId === userId;

    case 'parent':
      // Can only view groups their children are enrolled in
      return userChildrenCourses.includes(group.id);

    default:
      return false;
  }
};

/**
 * Get filtered course groups based on user role
 * @param {array} allGroups - All course groups
 * @param {string} role - User's role
 * @param {number} userId - Current user ID
 * @param {string} userCampus - User's campus (for manager)
 * @param {array} userChildrenCourses - Array of course IDs for parent's children
 * @returns {array} Filtered course groups
 */
export const getVisibleGroups = (allGroups, role, userId, userCampus = null, userChildrenCourses = []) => {
  return allGroups.filter(group =>
    canViewGroup(role, group, userId, userCampus, userChildrenCourses)
  );
};

/**
 * Check if user can create a new course group
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const canCreateGroup = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin'].includes(role);
};

/**
 * Check if user can edit a specific course group
 * @param {string} role - User's role
 * @param {object} group - Course group object
 * @param {number} userId - Current user ID
 * @param {string} userCampus - User's campus (for manager)
 * @returns {boolean}
 */
export const canEditGroup = (role, group, userId, userCampus = null, userCampusIds = []) => {
  switch (role) {
    case 'developer':
    case 'system_admin':
      // Can edit all groups
      return true;

    case 'director':
    case 'manager':
    case 'admin':
      // Can only edit groups in their campus(es)
      return matchesCampus(group.campusId, userCampus, userCampusIds);

    case 'teacher':
      // Can only edit their own groups (limited fields)
      return group.teacherId === userId;

    case 'parent':
      // Cannot edit any groups
      return false;

    default:
      return false;
  }
};

/**
 * Check if user can delete a course group
 * @param {string} role - User's role
 * @param {object} group - Course group object
 * @param {string} userCampus - User's campus (for manager)
 * @returns {boolean}
 */
export const canDeleteGroup = (role, group, userCampus = null, userCampusIds = []) => {
  switch (role) {
    case 'developer':
    case 'system_admin':
      // Can delete any group
      return true;

    case 'director':
    case 'manager':
    case 'admin':
      // Can only delete groups in their campus(es)
      return matchesCampus(group.campusId, userCampus, userCampusIds);

    case 'teacher':
    case 'parent':
      // Cannot delete groups
      return false;

    default:
      return false;
  }
};

/**
 * Check if user can post feedback to a course group
 * @param {string} role - User's role
 * @param {object} group - Course group object
 * @param {number} userId - Current user ID
 * @param {string} userCampus - User's campus (for manager)
 * @returns {boolean}
 */
export const canPostFeedback = (role, group, userId, userCampus = null, userCampusIds = []) => {
  switch (role) {
    case 'developer':
    case 'system_admin':
      // Can post feedback to any group
      return true;

    case 'director':
    case 'manager':
    case 'admin':
      // Can post feedback to groups in their campus(es)
      return matchesCampus(group.campusId, userCampus, userCampusIds);

    case 'teacher':
      // Can only post feedback to their own groups
      return group.teacherId === userId;

    case 'parent':
      // Cannot post feedback
      return false;

    default:
      return false;
  }
};

/**
 * Check if user can manage students (add/remove) in a course group
 * @param {string} role - User's role
 * @param {object} group - Course group object
 * @param {string} userCampus - User's campus (for manager)
 * @returns {boolean}
 */
export const canManageStudents = (role, group, userCampus = null, userCampusIds = []) => {
  switch (role) {
    case 'developer':
    case 'system_admin':
      // Can manage students in any group
      return true;

    case 'director':
    case 'manager':
    case 'admin':
      // Can only manage students in their campus groups
      return matchesCampus(group.campusId, userCampus, userCampusIds);

    case 'teacher':
      // Can manage students in their own groups
      return true; // Teacher can add/remove students from their classes

    case 'parent':
      // Cannot manage students
      return false;

    default:
      return false;
  }
};

/**
 * Check if user can assign/change the teacher for a course group
 * @param {string} role - User's role
 * @param {object} group - Course group object
 * @param {string} userCampus - User's campus (for manager)
 * @returns {boolean}
 */
export const canAssignTeacher = (role, group, userCampus = null, userCampusIds = []) => {
  switch (role) {
    case 'developer':
    case 'system_admin':
      // Can assign teachers to any group
      return true;

    case 'director':
    case 'manager':
    case 'admin':
      // Can only assign teachers to their campus groups
      return matchesCampus(group.campusId, userCampus, userCampusIds);

    case 'teacher':
    case 'parent':
      // Cannot assign teachers
      return false;

    default:
      return false;
  }
};

/**
 * Check if user can view detailed student information
 * @param {string} role - User's role
 * @param {object} student - Student object
 * @param {number} userId - Current user ID (parentId for parents)
 * @param {string} userCampus - User's campus (for manager)
 * @returns {boolean}
 */
export const canViewStudentDetails = (role, student, userId, userCampus = null, userCampusIds = []) => {
  switch (role) {
    case 'developer':
    case 'system_admin':
      // Can view all student details
      return true;

    case 'director':
    case 'manager':
    case 'admin':
      // Can view students in their campus(es)
      if (userCampusIds && userCampusIds.length > 0) {
        const studentCampuses = student.campuses || (student.campus ? [student.campus] : []);
        return studentCampuses.some(c => userCampusIds.includes(c));
      }
      return student.campus === userCampus;

    case 'teacher':
      // Can view students in their classes (check through course groups)
      return true; // Simplified - in real app, would check enrollment

    case 'parent':
      // Can only view their own children
      return student.parentId === userId;

    default:
      return false;
  }
};

/**
 * Get role display name
 * @param {string} role - Role key
 * @returns {string} Display name
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    developer: 'Developer',
    system_admin: 'System Admin',
    director: 'Director',
    manager: 'Manager',
    admin: 'Admin',
    teacher: 'Teacher',
    parent: 'Parent'
  };
  return roleNames[role] || role;
};

/**
 * Get role color for badges
 * @param {string} role - Role key
 * @returns {string} Color code
 */
export const getRoleColor = (role) => {
  const roleColors = {
    developer: '#0ea5e9', // Sky blue
    system_admin: '#dc2626', // Red
    director: '#f59e0b', // Orange
    manager: '#8b5cf6', // Purple
    admin: '#a855f7', // Light Purple
    teacher: '#10b981', // Green
    parent: '#3b82f6' // Blue
  };
  return roleColors[role] || '#6b7280';
};

/**
 * Check if role is an admin role (has elevated permissions)
 * @param {string} role - Role key
 * @returns {boolean}
 */
export const isAdminRole = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin'].includes(role);
};

/**
 * Check if user can filter by campus (unrestricted roles only)
 * @param {string} role - Role key
 * @returns {boolean}
 */
export const canFilterByCampus = (role) => {
  return ['developer', 'system_admin'].includes(role);
};

/**
 * Check if user can manage visibility restriction fields
 * (billingVisibleCampuses on students, visibleToCampuses on course groups).
 * Only system admin / developer may configure these — on purpose.
 *
 * Accepts either a single role key or an array of Cognito portal groups.
 */
export const canManageVisibilityRestrictions = (rolesOrGroups) => {
  if (!rolesOrGroups) return false;
  if (Array.isArray(rolesOrGroups)) {
    return rolesOrGroups.includes('portal_system_admin') ||
           rolesOrGroups.includes('portal_developer');
  }
  return ['developer', 'system_admin'].includes(rolesOrGroups);
};

// ==================== Course Group Specific Permissions ====================

/**
 * Check if user can edit all course group fields (vs just offset)
 * @param {string} role - Role key
 * @returns {boolean}
 */
export const canEditAllCourseGroupFields = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin'].includes(role);
};

/**
 * Check if user can only edit course group offset
 * @param {string} role - Role key
 * @returns {boolean}
 */
export const canOnlyEditCourseGroupOffset = (role) => {
  return false;
};

/**
 * Check if user can delete course groups.
 *
 * Accepts either a single role key ('manager') or an array of Cognito portal
 * groups (['portal_manager', ...]) — the latter is what the timetable page has
 * on hand and is not subject to the brief default-role flash the context has on
 * mount. Manager and above only; admin and teacher cannot. The backend enforces
 * the same set on the `delete` operation regardless.
 *
 * @param {string|string[]} rolesOrGroups - Role key, or array of Cognito groups
 * @returns {boolean}
 */
export const canDeleteCourseGroup = (rolesOrGroups) => {
  if (!rolesOrGroups) return false;
  if (Array.isArray(rolesOrGroups)) {
    return ['portal_developer', 'portal_system_admin', 'portal_director', 'portal_manager']
      .some(group => rolesOrGroups.includes(group));
  }
  return ['developer', 'system_admin', 'director', 'manager'].includes(rolesOrGroups);
};

/**
 * Check if user can manage course group students
 * @param {string} role - Role key
 * @returns {boolean}
 */
export const canManageCourseGroupStudents = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin'].includes(role);
};

// ==================== Test Management Permissions ====================

export const canAccessTestManagement = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin', 'teacher'].includes(role);
};

export const canCreateTestTemplate = (role) => {
  return ['developer', 'system_admin', 'director', 'manager'].includes(role);
};

/**
 * Who may create, rename or retire an exam batch.
 *
 * A batch only records which sitting a set of results belongs to, so this includes the front
 * desk: they collect the papers and type the scores in, and they are the ones who know a paper
 * is being re-used. Authoring the paper itself stays with canCreateTestTemplate. Deletion is
 * refused server-side for any batch that already has responses.
 */
export const canManageTestBatches = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin'].includes(role);
};

export const canEditTestTemplate = (role, template, userCampusIds) => {
  if (['developer', 'system_admin'].includes(role)) return true;
  if (role === 'director') return true;
  if (role === 'manager') {
    if (!template?.campusId) return false;
    return (userCampusIds || []).includes(template.campusId);
  }
  return false;
};

export const canDeleteTestTemplate = (role) => {
  return ['developer', 'system_admin'].includes(role);
};

export const canViewTestTemplate = (role, template, userCampusIds) => {
  if (['developer', 'system_admin', 'director'].includes(role)) return true;
  if (role === 'manager') {
    if (!template?.campusId) return true; // manager can see unassigned
    return (userCampusIds || []).includes(template.campusId);
  }
  if (role === 'admin') {
    if (!template?.campusId) return true; // admin can see unassigned (legacy templates)
    return (userCampusIds || []).includes(template.campusId);
  }
  if (role === 'teacher') {
    if (!template?.campusId) return true; // teacher can see unassigned (legacy templates)
    return true; // can see all assigned templates
  }
  return false;
};

export const canEditTestResponse = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin', 'teacher'].includes(role);
};

/**
 * Who may delete a test response outright.
 *
 * Campus management and the front desk, matching canArchiveResponses: a duplicate entry needs
 * clearing the same day it is noticed, not whenever a director is next free. Teachers enter and
 * mark responses but do not remove them. Enforced again in testDataFn, which until 2026-09-12
 * checked nothing at all on this operation.
 */
export const canDeleteTestResponse = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin'].includes(role);
};

export const canGenerateTestReport = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin'].includes(role);
};

// 内部查看完整分数排名（家长报告只显示前10名，内部人员可查全部，用于回应家长询问）
export const canViewTestRanking = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin'].includes(role);
};

export const canManageReportQueue = (role) => {
  return ['developer', 'system_admin', 'director'].includes(role);
};

export const canViewArchivedResponses = (role) => {
  return ['developer', 'system_admin', 'director', 'manager'].includes(role);
};

export const canArchiveResponses = (role) => {
  return ['developer', 'system_admin', 'director', 'manager', 'admin'].includes(role);
};

// ============================================
// Multi-Role Helper Functions
// ============================================

// Role priority order (highest to lowest)
const ROLE_PRIORITY = ['developer', 'system_admin', 'director', 'manager', 'admin', 'teacher', 'parent'];

/**
 * Check if user has any of the specified roles
 * @param {string[]} userRoles - Array of user's roles
 * @param {string[]} requiredRoles - Roles to check for
 * @returns {boolean}
 */
export const hasAnyRole = (userRoles, requiredRoles) => {
  return requiredRoles.some(r => userRoles.includes(r));
};

/**
 * Check if user has all of the specified roles
 * @param {string[]} userRoles - Array of user's roles
 * @param {string[]} requiredRoles - Roles required
 * @returns {boolean}
 */
export const hasAllRoles = (userRoles, requiredRoles) => {
  return requiredRoles.every(r => userRoles.includes(r));
};

/**
 * Get the highest privilege role from user's roles
 * @param {string[]} userRoles - Array of user's roles
 * @returns {string|null} The highest priority role or null
 */
export const getHighestRole = (userRoles) => {
  return ROLE_PRIORITY.find(r => userRoles.includes(r)) || null;
};

/**
 * Check if user has any admin-level role
 * @param {string[]} userRoles - Array of user's roles
 * @returns {boolean}
 */
export const hasAdminRole = (userRoles) => {
  const adminRoles = ['developer', 'system_admin', 'director', 'manager', 'admin'];
  return hasAnyRole(userRoles, adminRoles);
};

/**
 * Get roles that a user can assign based on their roles
 * Higher roles can only assign lower roles
 * @param {string[]} userRoles - Array of user's roles
 * @returns {string[]} Array of assignable roles
 */
export const getAssignableRoles = (userRoles) => {
  const highestRole = getHighestRole(userRoles);
  if (!highestRole) return [];

  const highestIndex = ROLE_PRIORITY.indexOf(highestRole);
  // Can assign roles lower than their highest role (excluding system_admin and developer)
  return ROLE_PRIORITY.slice(highestIndex + 1).filter(r => r !== 'system_admin' && r !== 'developer');
};
